<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDuration } from 'shared/helpers/timeHelper';

const props = defineProps({
  stats: { type: Object, default: () => ({}) },
  isFetching: { type: Boolean, default: false },
});

const { t } = useI18n();

const totals = computed(() => props.stats?.totals || {});
const duration = computed(() => props.stats?.duration || {});
const waiting = computed(() => props.stats?.waiting || {});
const agents = computed(() => props.stats?.agents || []);

// Talk time is read as a duration, not as a number of seconds: 1.284 seconds means nothing to
// someone running an operation.
const tiles = computed(() => [
  {
    key: 'calls',
    label: t('CALLS_PAGE.STATS.CALLS'),
    value: totals.value.calls ?? 0,
    hint: t('CALLS_PAGE.STATS.CALLS_SPLIT', {
      inbound: totals.value.inbound ?? 0,
      outbound: totals.value.outbound ?? 0,
    }),
  },
  {
    key: 'answered',
    label: t('CALLS_PAGE.STATS.ANSWERED'),
    value: totals.value.answered ?? 0,
    hint: t('CALLS_PAGE.STATS.ANSWER_RATE', {
      rate: props.stats?.answer_rate ?? 0,
    }),
  },
  {
    key: 'unanswered',
    label: t('CALLS_PAGE.STATS.UNANSWERED'),
    value: totals.value.unanswered ?? 0,
  },
  {
    key: 'talk',
    label: t('CALLS_PAGE.STATS.TALK_TIME'),
    value: formatDuration(duration.value.total_seconds ?? 0),
    hint: t('CALLS_PAGE.STATS.AVERAGE', {
      duration: formatDuration(duration.value.average_seconds ?? 0),
    }),
  },
  {
    key: 'longest',
    label: t('CALLS_PAGE.STATS.LONGEST'),
    value: formatDuration(duration.value.longest_seconds ?? 0),
  },
  {
    key: 'waiting',
    label: t('CALLS_PAGE.STATS.WAIT'),
    value: formatDuration(waiting.value.average_answer_seconds ?? 0),
  },
]);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      <div
        v-for="tile in tiles"
        :key="tile.key"
        class="flex flex-col gap-1 p-4 border rounded-xl border-n-weak bg-n-solid-1"
        :class="{ 'opacity-60': isFetching }"
      >
        <span class="text-sm text-n-slate-11">{{ tile.label }}</span>
        <span class="text-xl font-medium text-n-slate-12">{{
          tile.value
        }}</span>
        <span v-if="tile.hint" class="text-xs text-n-slate-10">
          {{ tile.hint }}
        </span>
      </div>
    </div>

    <div v-if="agents.length" class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('CALLS_PAGE.STATS.BY_AGENT') }}
      </span>
      <div class="overflow-hidden border rounded-xl border-n-weak">
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-n-alpha-1 text-n-slate-11">
              <th class="px-4 py-2 font-medium text-left">
                {{ t('CALLS_PAGE.STATS.AGENT') }}
              </th>
              <th class="px-4 py-2 font-medium text-right">
                {{ t('CALLS_PAGE.STATS.CALLS') }}
              </th>
              <th class="px-4 py-2 font-medium text-right">
                {{ t('CALLS_PAGE.STATS.TALK_TIME') }}
              </th>
              <th class="px-4 py-2 font-medium text-right">
                {{ t('CALLS_PAGE.STATS.AVERAGE_SHORT') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="agent in agents"
              :key="agent.id"
              class="border-t border-n-weak"
            >
              <td class="px-4 py-2 text-n-slate-12">{{ agent.name }}</td>
              <td class="px-4 py-2 text-right text-n-slate-11">
                {{ agent.calls }}
              </td>
              <td class="px-4 py-2 text-right text-n-slate-11">
                {{ formatDuration(agent.talk_seconds) }}
              </td>
              <td class="px-4 py-2 text-right text-n-slate-11">
                {{ formatDuration(agent.average_seconds) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
